import React, { useMemo, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Form, InputGroup, OverlayTrigger, Popover, Spinner, Table, Tooltip } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CreatableSelect from "react-select/creatable";
import { BsInfoCircleFill, BsTrash } from "react-icons/bs";
import { FaDownload, FaFileUpload } from "react-icons/fa";
import { downloadTemplate, parseUploadedFile } from "./excelTemplate";

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
};

const parsePickerDate = (value) => {
  const match = `${value || ""}`.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() + 1 !== month ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
};

const cleanAmountInput = (value) =>
  `${value || ""}`.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");

const formatAmount = (value) => {
  const raw = `${value || ""}`.replace(/[$,]/g, "").trim();
  if (!raw) return "";
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : value;
};

const buildLoanOption = (value) => {
  const trimmed = `${value || ""}`.trim();
  return trimmed ? { label: trimmed, value: trimmed } : null;
};

const loanSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const ExcelUploadTab = ({
  rows,
  setRows,
  propertyList = [],
  noteMaxLength = 500,
  isMasquerading = false,
  masqueradeBlockedMessage = "",
  loadingAccounts,
  isSubmitting,
  onSubmitRequest,
  onCancelRows,
  onRevalidateRow,
  toastError,
  failedRows,
  progressText,
}) => {
  const fileRef = useRef(null);
  const [showInvalidOnly, setShowInvalidOnly] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const validCount = rows.filter((row) => row.status === "valid" && row.submitStatus !== "success").length;
  const invalidCount = rows.filter((row) => row.status === "invalid").length;
  const totalCount = rows.length;

  const loanNumberOptions = useMemo(() => {
    const values = new Set();
    propertyList.forEach((property) => {
      const account = property?.account || property?.Account || property?.loanNumber || property?.LoanNumber || "";
      if (account) values.add(account);
    });
    return Array.from(values).map(buildLoanOption).filter(Boolean);
  }, [propertyList]);

  const propertyByLoan = useMemo(() => {
    const map = new Map();
    propertyList.forEach((property) => {
      const key = `${property?.account || property?.Account || property?.loanNumber || property?.LoanNumber || ""}`.trim().toLowerCase();
      if (key) map.set(key, property);
    });
    return map;
  }, [propertyList]);

  const loanStatsFor = (loanNo) => {
    const property = propertyByLoan.get(`${loanNo || ""}`.trim().toLowerCase());
    return {
      loanAmount: property?.loanAmount || "--",
      unpaidPrincipalBalance: property?.unpaidPrincipalBalance || "--",
      availableFunds: property?.availableFunds || "--",
    };
  };

  const visibleRows = useMemo(
    () =>
      rows
        .map((row, originalIndex) => ({ row, originalIndex }))
        .filter(({ row }) => !showInvalidOnly || row.status === "invalid"),
    [rows, showInvalidOnly]
  );

  const processFile = async (file) => {
    try {
      const parsedRows = await parseUploadedFile(file);
      if (parsedRows.length > 100) {
        toastError("Excel has more than 100 rows. Please split into multiple uploads.");
        return;
      }
      setRows(
        parsedRows.map((row) => ({
          ...row,
          drawAmount: formatAmount(row.drawAmount),
          submitStatus: "pending",
          submitError: "",
        }))
      );
    } catch (error) {
      toastError(error?.message || "Unable to parse file.");
    } finally {
      setIsProcessingUpload(false);
    }
  };

  const openFilePicker = () => {
    if (rows.length > 0 && !window.confirm("Replace current rows with a new upload?")) return;
    if (fileRef.current) fileRef.current.value = "";
    fileRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadingFileName(file.name || "");
      setIsProcessingUpload(true);
      processFile(file);
    }
    event.target.value = "";
  };

  const updateRowField = (index, field, value) => {
    setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));
  };

  const formatRowAmount = (index, value) => {
    updateRowField(index, "drawAmount", formatAmount(value));
  };

  const deleteRow = (index) => {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const fieldError = (row, field) => row?.fieldErrors?.[field] || "";

  const loanSelectValue = (loanNo) =>
    loanNumberOptions.find((option) => option.value === loanNo) || buildLoanOption(loanNo);

  return (
    <div className="excel-upload-workspace mt-4">
      <div className="excel-upload-toolbar">
        {rows.length > 0 && (
          <>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="outline-primary" onClick={downloadTemplate} disabled={isSubmitting}>
                Download Template
              </Button>
              <Button variant="outline-secondary" onClick={openFilePicker} disabled={isSubmitting}>
                Re-upload
              </Button>
            </div>
            <div className="excel-upload-meta">
              <div className="excel-upload-stats">
                <span className="excel-stat success">{validCount} valid</span>
                <span className={invalidCount ? "excel-stat danger" : "excel-stat"}>{invalidCount} invalid</span>
                <span className="excel-stat">{totalCount} total</span>
              </div>
              <ButtonGroup size="sm" aria-label="Excel row filter">
                <Button
                  variant={!showInvalidOnly ? "primary" : "outline-primary"}
                  onClick={() => setShowInvalidOnly(false)}
                  disabled={isSubmitting}
                >
                  All Rows
                </Button>
                <Button
                  variant={showInvalidOnly ? "primary" : "outline-primary"}
                  onClick={() => setShowInvalidOnly(true)}
                  disabled={isSubmitting}
                >
                  Invalid Only
                </Button>
              </ButtonGroup>
            </div>
          </>
        )}
        <Form.Control ref={fileRef} type="file" className="d-none" accept=".xlsx" onChange={handleFileChange} />
      </div>

      {loadingAccounts && <div className="small text-muted mb-3">Loading your accounts...</div>}
      {rows.length === 0 && (
        <div className="excel-empty-state">
          {isProcessingUpload ? (
            <div className="excel-upload-progress">
              <Spinner animation="border" size="sm" />
              <div className="excel-upload-progress-text">
                <div className="excel-upload-progress-title">Processing your file...</div>
                <div className="excel-upload-progress-subtitle">{uploadingFileName || "Reading Excel rows"}</div>
              </div>
            </div>
          ) : (
            <div className="excel-launchpad">
              <div className="excel-launchpad-head">
                <div className="excel-upload-intro-title">Upload Draws in 2 Steps</div>
                <div className="excel-upload-intro-subtitle">Download the template, fill rows, then upload your file.</div>
                <div className="excel-stepper">
                  <div className="excel-step-item">
                    <span className="excel-step-dot">1</span>
                    <span>Download Template</span>
                  </div>
                  <span className="excel-step-line" />
                  <div className="excel-step-item excel-step-item-active">
                    <span className="excel-step-dot">2</span>
                    <span>Upload File</span>
                  </div>
                </div>
              </div>
              <div className="excel-upload-cards">
                <div className="excel-action-card excel-action-card-download">
                  <div className="excel-action-icon-wrap">
                    <FaDownload className="excel-action-icon excel-action-icon-download" />
                  </div>
                  <div className="excel-action-title">Step 1: Download Template</div>
                  <div className="excel-action-subtitle">Use this format to avoid validation errors.</div>
                  <Button className="excel-card-btn excel-card-btn-download" onClick={downloadTemplate} disabled={isSubmitting}>
                    <FaDownload className="me-2" />
                    Download Template
                  </Button>
                </div>
                <div className="excel-action-card excel-action-card-upload excel-action-card-primary">
                  <div className="excel-action-icon-wrap excel-action-icon-wrap-upload">
                    <FaFileUpload className="excel-action-icon excel-action-icon-upload" />
                  </div>
                  <div className="excel-action-title">Step 2: Upload File</div>
                  <div className="excel-action-subtitle">Select your completed file and continue.</div>
                  <div className="excel-upload-helper">Accepts `.xlsx` • Max 100 rows</div>
                  <Button className="excel-card-btn excel-card-btn-upload" onClick={openFilePicker} disabled={isSubmitting}>
                    <FaFileUpload className="me-2" />
                    Select Excel File
                  </Button>
                </div>
              </div>
              <div className="excel-launchpad-footer">
                <div className="excel-launchpad-footer-item">
                  <span className="excel-launchpad-footer-label">Required columns</span>
                  <span className="excel-launchpad-footer-value">Loan Number, Draw Amount, Actual Draw Disbursement Date</span>
                </div>
                <div className="excel-launchpad-footer-item">
                  <span className="excel-launchpad-footer-label">Notes (optional)</span>
                  <span className="excel-launchpad-footer-value">Free-text instructions for our team about each draw (e.g., how to allocate funds). Max 500 characters.</span>
                </div>
                <div className="excel-launchpad-footer-item">
                  <span className="excel-launchpad-footer-label">Disbursement Date format</span>
                  <span className="excel-launchpad-footer-value">MM/DD/YYYY only (e.g., 11/04/2026). Other formats are rejected.</span>
                </div>
                <div className="excel-launchpad-footer-item">
                  <span className="excel-launchpad-footer-label">Draw Amount</span>
                  <span className="excel-launchpad-footer-value">Number greater than 0. The $ sign and commas are optional (e.g., $1,250.00 or 1250).</span>
                </div>
                <div className="excel-launchpad-footer-item">
                  <span className="excel-launchpad-footer-label">Loan Number</span>
                  <span className="excel-launchpad-footer-value">Must match an account in your portfolio.</span>
                </div>
                <div className="excel-launchpad-footer-item">
                  <span className="excel-launchpad-footer-label">Limit</span>
                  <span className="excel-launchpad-footer-value">Up to 100 rows per upload.</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {rows.length > 0 && (
        <>
          <div className="excel-upload-rules small text-muted mb-2">
            <strong>Format:</strong> Actual Draw Disbursement Date must be MM/DD/YYYY · Draw Amount can be entered with or without $ (e.g., $1,250.00 or 1250) · Loan Number must match an account in your portfolio · Notes are optional instructions for our team.
          </div>
          <div className="excel-grid-shell">
            <Table bordered size="sm" className="excel-upload-table mb-0">
              <thead>
                <tr>
                  <th className="excel-row-number">#</th>
                  <th>Loan Number</th>
                  <th>Draw Amount</th>
                  <th>Actual Draw Disbursement Date</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th className="excel-delete-col">Delete</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(({ row, originalIndex }) => (
                  <tr className={row.status === "invalid" ? "excel-row-invalid" : ""} key={`${originalIndex}-${row.loanNo}`}>
                    <td className="excel-row-number">{originalIndex + 1}</td>
                    <td>
                      <div className="excel-loan-cell">
                        <CreatableSelect
                          className={`excel-loan-select ${fieldError(row, "loanNo") ? "is-invalid" : ""}`}
                          classNamePrefix="excel-loan-select"
                          value={loanSelectValue(row.loanNo)}
                          options={loanNumberOptions}
                          placeholder="Select or type loan"
                          isClearable
                          isDisabled={isSubmitting}
                          menuPosition="fixed"
                          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                          styles={loanSelectStyles}
                          formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                          onChange={(selected) => updateRowField(originalIndex, "loanNo", selected?.value || "")}
                          onCreateOption={(value) => updateRowField(originalIndex, "loanNo", value)}
                          onBlur={() => onRevalidateRow(originalIndex)}
                        />
                        {propertyByLoan.has(`${row.loanNo || ""}`.trim().toLowerCase()) && (
                          <OverlayTrigger
                            trigger={["hover", "focus", "click"]}
                            placement="top"
                            rootClose
                            overlay={(() => {
                              const stats = loanStatsFor(row.loanNo);
                              return (
                                <Popover className="excel-loan-stats-popover">
                                  <Popover.Header as="div">Loan {row.loanNo}</Popover.Header>
                                  <Popover.Body>
                                    <div className="excel-loan-stat-row">
                                      <span className="excel-loan-stat-label">Loan Amount</span>
                                      <span className="excel-loan-stat-value">{stats.loanAmount}</span>
                                    </div>
                                    <div className="excel-loan-stat-row">
                                      <span className="excel-loan-stat-label">Unpaid Principal Balance</span>
                                      <span className="excel-loan-stat-value">{stats.unpaidPrincipalBalance}</span>
                                    </div>
                                    <div className="excel-loan-stat-row">
                                      <span className="excel-loan-stat-label">Available Funds</span>
                                      <span className="excel-loan-stat-value">{stats.availableFunds}</span>
                                    </div>
                                  </Popover.Body>
                                </Popover>
                              );
                            })()}
                          >
                            <button
                              type="button"
                              className="excel-loan-info-btn"
                              aria-label={`Loan stats for ${row.loanNo}`}
                            >
                              <BsInfoCircleFill />
                            </button>
                          </OverlayTrigger>
                        )}
                      </div>
                    </td>
                    <td>
                      <InputGroup className={fieldError(row, "drawAmount") ? "excel-input-group-invalid" : ""}>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          className={fieldError(row, "drawAmount") ? "excel-control-invalid" : ""}
                          value={row.drawAmount || ""}
                          placeholder="0.00"
                          disabled={isSubmitting}
                          onChange={(event) => updateRowField(originalIndex, "drawAmount", cleanAmountInput(event.target.value))}
                          onBlur={(event) => formatRowAmount(originalIndex, event.target.value)}
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <DatePicker
                        selected={parsePickerDate(row.drawDate)}
                        value={row.drawDate || ""}
                        onChange={(date) => {
                          updateRowField(originalIndex, "drawDate", date ? formatDate(date) : "");
                        }}
                        onChangeRaw={(event) => updateRowField(originalIndex, "drawDate", event?.target?.value || "")}
                        onBlur={() => onRevalidateRow(originalIndex)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        className={`form-control draw-date-input ${fieldError(row, "drawDate") ? "excel-control-invalid" : ""}`}
                        disabled={isSubmitting}
                        portalId="root"
                        popperClassName="excel-date-popper"
                        showPopperArrow={false}
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        className="excel-note-input"
                        value={row.notes || ""}
                        maxLength={noteMaxLength}
                        placeholder="Optional note for our team"
                        disabled={isSubmitting}
                        onChange={(event) => updateRowField(originalIndex, "notes", event.target.value)}
                      />
                    </td>
                    <td>
                      {row.status === "valid" ? <Badge bg="success">Valid</Badge> : <Badge bg="danger">Invalid</Badge>}
                    </td>
                    <td className="small excel-reason-cell" title={row.reasons?.join(", ") || row.submitError || ""}>
                      {row.reasons?.join(", ") || row.submitError || "-"}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="light"
                        size="sm"
                        className="excel-delete-btn"
                        disabled={isSubmitting}
                        onClick={() => deleteRow(originalIndex)}
                        title="Delete row"
                      >
                        <BsTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No invalid rows.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="excel-action-bar">
            <div>
              <div className="small fw-semibold">{validCount} valid / {invalidCount} invalid / {rows.length} total</div>
              <div className="excel-action-hint">You can submit valid rows now and fix invalid rows later.</div>
            </div>
            <div className="d-flex gap-2">
              {isMasquerading ? (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="excel-submit-masquerade-tip" className="masquerade-tooltip">{masqueradeBlockedMessage}</Tooltip>}
                >
                  {/* A disabled button emits no hover events, so the tooltip has to
                      hang off an enabled wrapper instead of the button itself. */}
                  <span className="d-inline-block">
                    <Button variant="primary" disabled style={{ pointerEvents: "none" }} onClick={onSubmitRequest}>
                      Submit {validCount} Valid Rows
                    </Button>
                  </span>
                </OverlayTrigger>
              ) : (
                <Button variant="primary" disabled={validCount === 0 || isSubmitting} onClick={onSubmitRequest}>
                  Submit {validCount} Valid Rows
                </Button>
              )}
              <Button variant="secondary" disabled={isSubmitting} onClick={onCancelRows}>Cancel</Button>
            </div>
          </div>
          {progressText && <div className="small mt-2">{progressText}</div>}
        </>
      )}
      {failedRows.length > 0 && (
        <div className="mt-3">
          <div className="fw-semibold mb-2">Failed submissions</div>
          <Table bordered size="sm">
            <thead><tr><th>Loan Number</th><th>Draw Amount</th><th>Actual Draw Disbursement Date</th><th>Error</th></tr></thead>
            <tbody>
              {failedRows.map((row, idx) => (
                <tr key={`failed-${idx}`}>
                  <td>{row.loanNo}</td><td>{row.drawAmount}</td><td>{row.drawDate}</td><td>{row.submitError}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ExcelUploadTab;
